import React from "react";
import {
  Box,
  Button,
  CardMedia,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";
import { styled } from "@mui/system";
import QrCode2TwoToneIcon from "@mui/icons-material/QrCode2TwoTone";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import api from "../api/api";
import LoginDialog from "../component/LoginDialog";
import { useTheme } from "@mui/material/styles";
import verifyToken from "../api/user/verifyToken";
import FadeWrapper from "../component/FadeWrapper";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import QRCodeDialog from "../component/QRCodeDialog";
import getOrders from "../api/app/getOrders";
import getRoom from "../api/app/getRoom";
import getSimulatorReservations from "../api/app/getSimulatorReservations";
import RoomList from "../component/RoomList";


const FullWidthButton = styled(Button)(({ theme }) => ({
  width: "calc(100% - 5px)", // subtract the margin from the width
  height: "14.5vh",
  fontSize: "24pt",
  fontWeight: "bold",
  border: "1px solid #555555",
  borderRadius: "5px",
  marginBottom: "1px",
}));

const SecondFullWidthButton = styled(Button)(({ theme }) => ({
  width: "33%",
  height: "4vh",
  fontSize: "10pt",
  fontWeight: "bold",
  color: "rgb(56, 56, 56)",
  border: "1px rgb(123, 123, 123)",
  backgroundColor: "white",
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  borderRadius: "10px",
  height: "32vh",
  margin: "5px 2px",
}));

const SecondCardMedia = styled(CardMedia)(({ theme }) => ({
  height: "15vh",
  borderRadius: "10px",
  margin: "5px 1px",
}));

const MainPage = () => {
  const navigate = useNavigate();
  const { storeId } = useParams();
  const theme = useTheme();
  const [loginOpen, setLoginOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openQRCode, setOpenQRCode] = useState(false);
  const [qrStrings, setQrStrings] = useState([]);
  const handleLoginOpen = () => setLoginOpen(true);
  const userContext = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [selectedTables, setSelectedTables] = useState(null);
  const [store, setStore] = useState(null);
  const handleSelectRoom = (tables, storeInfo) => {
    setSelectedTables(tables);
    setStore(storeInfo);
  };

  const handleBackToRoomList = () => {
    setSelectedTables(null);
    setStore(null);
  };
  const handleButtonClick = (path) => {
    navigate(path);
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("token");

    if (storedToken) {
      api.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
      setToken(storedToken);
      userContext.setUser(storedToken)
    }
  }, []);

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based, so add 1
    const day = String(date.getDate()).padStart(2, "0"); // Pad single digit days with a zero

    return `${year}-${month}-${day}`;
  }

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getOrders(); // Call the getOrders function
        console.log("[MainPage] getOrders response:", response);
        if (response !== null) {
          const orderIds = getCurrentOrderIds(response.data); // Get the current order IDs
          console.log("[MainPage] getCurrentOrderIds:", orderIds);
          return orderIds; // Return the filtered order IDs
        } else {
          setToken(null); // Set token to null if there's no response
        }
      } catch (error) {
        console.error("Failed to fetch orders", error);
        setToken(null); // Set token to null if there's an error
      }
    }

    const getCurrentOrderIds = (data) => {
      const currentTime = new Date();

      return data
        .filter((item) => {
          const startTime = new Date(item.startTime);

          const endTime = new Date(item.endTime);

          return currentTime >= startTime && currentTime <= endTime; // Filter orders between start and end time
        })
        .map((item) => item.order.id); // Map to order IDs
    };

    console.log("[MainPage] useEffect triggered, token:", token);
    if (token) {
      fetchData().then((orderIds) => {
        if (orderIds) {

          setQrStrings([orderIds]); // Set the QR strings with the returned order IDs
        }
      });
    }
  }, [token]);

  function logout() {
    localStorage.removeItem("token");
    delete api.defaults.headers.common["Authorization"];
    userContext.setUser(null);
    setToken(null);
  }

  return (
    <FadeWrapper>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          flexDirection: "column",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        <AppBar
          position="static"
          elevation={0}
          sx={{ backgroundColor: theme.palette.appbar }}
        >
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            {/* Left Section (Logo) */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton edge="start" color="inherit" aria-label="logo">
                <img src={`${process.env.REACT_APP_API_ENDPOINT
                  }/media/${storeId}.png?${new Date().getTime()}`} alt="Logo" style={{ height: 60 }} />
              </IconButton>
            </Box>

            {/* Right Section (Auth Buttons) */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {token !== null && (
                <AccountCircleIcon
                  color="primary"
                  sx={{ marginRight: 1, cursor: "pointer" }}
                  onClick={() => {
                    handleButtonClick("/app/profile");
                  }}
                />
              )}
              {token !== null ? (
                <Button
                  sx={{ fontWeight: "bold", color: "#878787" }}
                  onClick={logout}
                >
                  登出
                </Button>
              ) : (
                <Button
                  color="primary"
                  onClick={handleLoginOpen}
                  sx={{ fontWeight: "bold", color: "#878787" }}
                >
                  登入/註冊
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          marginX={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <SecondFullWidthButton
            variant="contained"
            sx={{ marginX: 1 }}
            startIcon={<QrCode2TwoToneIcon fontSize="large" />}
            onClick={() => {
              setOpenQRCode(!openQRCode);
            }}
          >
            入場碼
          </SecondFullWidthButton>
          <SecondFullWidthButton
            variant="contained"
            onClick={() => {
              handleButtonClick("/app/myOrder");
            }}
          >
            我的訂單
          </SecondFullWidthButton>
        </Box>
        <RoomList
          token={token}
          storeId={storeId}
          onSelectRoom={(tables, storeInfo) => {
            setSelectedTables(tables);
            setStore(storeInfo);
          }}
        />


      </Box>

      <LoginDialog
        openLogin={loginOpen}
        handleLoginClose={() => setLoginOpen(false)}
        setToken={setToken}
      />
      <QRCodeDialog
        open={openQRCode}
        onClose={() => setOpenQRCode(false)}
        qrStrings={qrStrings}
        infoString={token !== null ? "入場碼" : "尚未登入"}
      />
    </FadeWrapper>
  );
};

export default MainPage;
